/* Whale Tail hero fallback + playback detection (ported verbatim from the prototype).
   1) JS mug-tunnel "boot" - the live fallback animation. Only runs when the baked video
      can't actually play (iOS Low Power Mode / strict in-app webviews). Defines
      window.__heroMugLoop so the detector can start it.
   2) Real-playback detection - commits to the <video> only when frames actually advance
      (timeupdate / currentTime), NOT play()'s promise (iOS resolves it while blocked).
   Loaded deferred AFTER wtm-whale-tail.js so the DOM is fully parsed. */
(() => {
  function boot() {
    const mugs = Array.from(document.querySelectorAll("[data-loop-mug]"));
    if (!mugs.length) return;

    const hero = document.querySelector(".depth-loop-hero");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const compactMotion = window.matchMedia("(max-width: 700px)");
    const duration = 52000;
    let loopVisible = true;
    let loopFrame = null;
    // Start mid-lunge so the hero opens on an already-zoomed-in mug, not a build-up.
    let loopStart = performance.now() - duration * (compactMotion.matches ? 0.88 : 0.92);

    document.documentElement.dataset.inlineMugLoop = "true";

    function fadeEdge(value, start, end) {
      if (value <= start) return 0;
      if (value >= end) return 1;
      return (value - start) / (end - start);
    }

    function smoothEdge(value, start, end) {
      const t = fadeEdge(value, start, end);
      return t * t * (3 - 2 * t);
    }

    function paint(now) {
      const spacing = 1 / mugs.length;
      const compact = compactMotion.matches;
      const time = (((now - loopStart) % duration) / duration + 1) % 1;

      mugs.forEach((mug, index) => {
        const phase = (time + index * spacing) % 1;
        const ACCEL = compact ? 60 : 90;     // ratio ~1.67x (mobile) / ~1.76x (desktop)
        const BASE = compact ? 0.028 : 0.0205;
        const MAXSCALE = compact ? 1.75 : 1.9;
        const scale = Math.min(MAXSCALE, BASE * Math.pow(ACCEL, phase));
        const x = (compact ? 10 : 16) - phase * (compact ? 20 : 34);
        const y = Math.sin((phase + index * spacing) * Math.PI * 2) * (compact ? 2 : 3);

        const fadeIn = fadeEdge(phase, 0.01, 0.04);
        const fadeOutStart = compact ? 0.93 : 0.942;
        const fadeOutEnd = compact ? 0.997 : 0.998;
        const finalFade = 1 - smoothEdge(phase, fadeOutStart, fadeOutEnd);
        const opacity = Math.max(0, Math.min(1, fadeIn * finalFade));
        const z = String(Math.round(phase * 20));

        mug.style.setProperty("--loop-scale", scale.toFixed(4));
        mug.style.setProperty("--loop-x", `${x.toFixed(2)}px`);
        mug.style.setProperty("--loop-y", `${y.toFixed(2)}px`);
        mug.style.setProperty("--loop-opacity", opacity.toFixed(4));
        if (mug.dataset.loopZ !== z) {
          mug.dataset.loopZ = z;
          mug.style.zIndex = z;
        }
      });
    }

    function frame(now) {
      loopFrame = null;
      paint(now);
      if (!reduceMotion.matches && loopVisible) {
        loopFrame = requestAnimationFrame(frame);
      }
    }

    function startLoop() {
      if (loopFrame !== null) {
        cancelAnimationFrame(loopFrame);
        loopFrame = null;
      }

      if (reduceMotion.matches) {
        paint(loopStart + duration * (compactMotion.matches ? 0.72 : 0.82));
        return;
      }

      if (loopVisible) {
        loopFrame = requestAnimationFrame(frame);
      }
    }

    if ("IntersectionObserver" in window && hero) {
      const loopObserver = new IntersectionObserver(
        entries => {
          loopVisible = entries.some(entry => entry.isIntersecting);
          if (loopVisible) {
            startLoop();
          } else if (loopFrame !== null) {
            cancelAnimationFrame(loopFrame);
            loopFrame = null;
          }
        },
        { rootMargin: "240px 0px" }
      );
      loopObserver.observe(hero);
    }

    reduceMotion.addEventListener?.("change", startLoop);
    compactMotion.addEventListener?.("change", () => {
      loopStart = performance.now() - duration * (compactMotion.matches ? 0.7 : 0.82);
      startLoop();
    });
    startLoop();
  }
  window.__heroMugLoop = { start: boot };
  if (document.documentElement.dataset.hero === "js") boot();
})();

(() => {
  const v = document.querySelector(".loop-video");
  if (!v) return;
  v.muted = true; v.defaultMuted = true; v.playsInline = true;
  let settled = false;
  function useJs() {
    if (settled) return; settled = true;
    document.documentElement.dataset.hero = "js";
    document.querySelectorAll(".loop-mug[data-src]").forEach(img => {
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
    });
    try { v.pause(); } catch (e) {}
    if (window.__heroMugLoop) window.__heroMugLoop.start();
  }
  function useVideo() {
    if (settled) return; settled = true;
    document.documentElement.dataset.hero = "video";
  }
  v.addEventListener("timeupdate", () => { if (v.currentTime > 0.05) useVideo(); });
  v.play().catch(() => {});
  let tries = 0;
  function decide() {
    if (settled) return;
    if (!v.paused && v.currentTime > 0.05) return useVideo();  // genuinely playing
    if (v.readyState >= 2 || ++tries >= 8) return useJs();      // has data but paused (Low Power) -> JS
    setTimeout(decide, 400);                                    // still loading -> re-check
  }
  const kick = () => setTimeout(decide, 600);
  if (document.readyState === "complete") kick();
  else window.addEventListener("load", kick);
})();
